import React, { useEffect, useState } from 'react';
import toast from 'react-hot-toast';
import DownloadIcon from '@mui/icons-material/Download';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import downloadReport from '../../utils/downloadReport';
import { maskEmail } from '../../utils/studentPrivacy';
import {
  RANKING_PAGE_SIZE,
  exportPath,
  siError,
  useRanking,
} from '../../hooks/useStudentRankings';
import {
  CARD,
  COMPONENTS,
  ComponentBars,
  INPUT,
  componentLabel,
  formatDay,
  percent,
  score,
} from './rankingText';

const fraction = (done, total) => (total ? `${done ?? 0}/${total}` : '–');

export default function Leaderboard({
  cohortId,
  modules,
  privacyRestricted,
  onOpenLearner,
}) {
  const [moduleId, setModuleId] = useState('');
  const [sort, setSort] = useState('score');
  const [search, setSearch] = useState('');
  const [q, setQ] = useState('');
  const [offset, setOffset] = useState(0);
  const [exporting, setExporting] = useState(false);

  // A new cohort starts from the top with no filters.
  useEffect(() => {
    setModuleId('');
    setSort('score');
    setSearch('');
    setQ('');
    setOffset(0);
  }, [cohortId]);

  useEffect(() => {
    const timer = setTimeout(() => {
      setQ(search.trim());
      setOffset(0);
    }, 400);
    return () => clearTimeout(timer);
  }, [search]);

  const { data, isLoading, error, isFetching } = useRanking(
    cohortId,
    { moduleId, sort, q },
    offset
  );
  const rows = data?.students ?? [];
  const total = data?.total ?? 0;
  // Engagement only counts towards the overall score.
  const sorts = [
    'score',
    ...COMPONENTS.filter((c) => !(moduleId && c === 'engagement')),
  ];

  const exportCsv = async () => {
    setExporting(true);
    try {
      await downloadReport(exportPath(cohortId), 'student-rankings.csv', {
        params: moduleId ? { module_id: moduleId } : undefined,
      });
    } catch (err) {
      toast.error(err.message);
    } finally {
      setExporting(false);
    }
  };

  return (
    <div className="space-y-4">
      <div className={`${CARD} flex flex-wrap items-center gap-3 p-4`}>
        <select
          aria-label="Module"
          value={moduleId}
          onChange={(e) => {
            setModuleId(e.target.value);
            if (e.target.value && sort === 'engagement') setSort('score');
            setOffset(0);
          }}
          className={`${INPUT} max-w-xs`}
        >
          <option value="">All modules (overall)</option>
          {modules.map((m) => (
            <option key={m.module_id} value={m.module_id}>
              {m.name}
              {m.kind === 'specialization' ? ' (specialization)' : ''}
            </option>
          ))}
        </select>
        <label className="flex items-center gap-2 text-xs text-gray-600">
          Rank by
          <select
            aria-label="Rank by"
            value={sort}
            onChange={(e) => {
              setSort(e.target.value);
              setOffset(0);
            }}
            className={INPUT}
          >
            {sorts.map((key) => (
              <option key={key} value={key}>
                {key === 'score' ? 'Overall score' : componentLabel(key)}
              </option>
            ))}
          </select>
        </label>
        <input
          type="search"
          aria-label="Search learners"
          placeholder="Search name, email or ID"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          className={`${INPUT} w-60`}
        />
        <button
          type="button"
          onClick={exportCsv}
          disabled={exporting || !total}
          className="ml-auto inline-flex items-center gap-1 rounded-lg border border-gray-300 bg-white px-3 py-1.5 text-sm text-gray-700 hover:bg-gray-50 disabled:opacity-50"
        >
          <DownloadIcon fontSize="small" />
          {exporting ? 'Exporting…' : 'Export CSV'}
        </button>
      </div>

      {data && (
        <p className="px-1 text-xs text-gray-500">
          {data.members?.toLocaleString()} learners · average score{' '}
          {score(data.average_score)}
          {moduleId && ' on this module'}
          {isFetching && ' · loading…'}
        </p>
      )}

      {isLoading && (
        <div className={`${CARD} h-64 animate-pulse`} aria-label="Loading" />
      )}
      {error && !data && (
        <div className={`${CARD} p-8 text-center text-sm text-gray-600`}>
          {siError(error, "We couldn't load the rankings.")}
        </div>
      )}
      {data && total === 0 && (
        <div className={`${CARD} p-10 text-center text-sm text-gray-600`}>
          No learners match.
        </div>
      )}
      {data && total > 0 && (
        <section className={`${CARD} overflow-hidden`}>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-gray-200 bg-gray-50 text-left text-[11px] uppercase tracking-wide text-gray-500">
                  <th className="py-2 pl-5 pr-2 font-medium">Rank</th>
                  <th className="px-2 py-2 font-medium">Learner</th>
                  <th className="px-2 py-2 text-right font-medium">Score</th>
                  <th className="px-2 py-2 font-medium">Breakdown</th>
                  <th className="px-2 py-2 text-right font-medium">Quiz avg</th>
                  <th className="px-2 py-2 text-right font-medium">
                    Assignment avg
                  </th>
                  <th className="px-2 py-2 text-right font-medium">Weeks</th>
                  <th className="px-2 py-2 font-medium">Last login</th>
                  <th className="py-2 pl-2 pr-5" aria-label="Open" />
                </tr>
              </thead>
              <tbody>
                {rows.map((row) => {
                  const d = row.details ?? {};
                  return (
                    <tr
                      key={row.student_id}
                      onClick={() => onOpenLearner(row.student_id)}
                      className="cursor-pointer border-b border-gray-100 last:border-0 hover:bg-gray-50"
                    >
                      <td className="py-2.5 pl-5 pr-2 font-semibold tabular-nums text-gray-900">
                        {row.rank}
                      </td>
                      <td className="px-2 py-2.5">
                        <span className="text-gray-900">{row.name || '–'}</span>
                        <span className="block text-xs text-gray-500">
                          {privacyRestricted ? maskEmail(row.email) : row.email}
                        </span>
                      </td>
                      <td className="px-2 py-2.5 text-right font-semibold tabular-nums text-gray-900">
                        {score(row.score)}
                      </td>
                      <td className="px-2 py-2.5">
                        <ComponentBars components={row.components} />
                      </td>
                      <td className="px-2 py-2.5 text-right tabular-nums text-gray-700">
                        {percent(d.quiz_first_attempt_avg)}
                        <span className="block text-[11px] text-gray-400">
                          {fraction(d.quizzes_taken, d.quizzes_total)} taken
                        </span>
                      </td>
                      <td className="px-2 py-2.5 text-right tabular-nums text-gray-700">
                        {percent(d.assignment_avg_grade)}
                        <span className="block text-[11px] text-gray-400">
                          {fraction(
                            d.assignments_submitted,
                            d.assignments_total
                          )}{' '}
                          submitted
                        </span>
                      </td>
                      <td className="px-2 py-2.5 text-right tabular-nums text-gray-700">
                        {fraction(d.weeks_done, d.weeks_total)}
                      </td>
                      <td className="whitespace-nowrap px-2 py-2.5 text-xs text-gray-600">
                        {moduleId ? '–' : formatDay(d.last_login)}
                      </td>
                      <td className="py-2.5 pl-2 pr-5 text-right text-gray-400">
                        <ChevronRightIcon fontSize="small" />
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
          <div className="flex items-center justify-between border-t border-gray-200 px-5 py-2 text-xs text-gray-500">
            <span>
              {(offset + 1).toLocaleString()}–
              {Math.min(offset + rows.length, total).toLocaleString()} of{' '}
              {total.toLocaleString()}
            </span>
            <div className="flex gap-2">
              <button
                type="button"
                disabled={offset === 0}
                onClick={() =>
                  setOffset(Math.max(0, offset - RANKING_PAGE_SIZE))
                }
                className="rounded-md border border-gray-300 px-3 py-1 hover:bg-gray-50 disabled:opacity-40"
              >
                Previous
              </button>
              <button
                type="button"
                disabled={offset + RANKING_PAGE_SIZE >= total}
                onClick={() => setOffset(offset + RANKING_PAGE_SIZE)}
                className="rounded-md border border-gray-300 px-3 py-1 hover:bg-gray-50 disabled:opacity-40"
              >
                Next
              </button>
            </div>
          </div>
        </section>
      )}
    </div>
  );
}
